/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  NativeSelect,
  NativeSelectOption,
} from '@/components/ui/native-select'
import { Dialog } from '@/components/dialog'
import { createModel, getModel, updateModel } from '../../api'
import type { Model, ModelFieldSyncItem } from '../../types'
import {
  EDITABLE_FEATURES,
  buildCapabilities,
  extractEditValues,
  type CapabilityEditValues,
} from '../../lib/capability-edit'

const INPUT_MODALITIES = ['text', 'image', 'audio', 'video', 'file'] as const
const OUTPUT_MODALITIES = ['text', 'image', 'audio', 'video'] as const

// Non-DB (`gorm:"-"`) fields on Model that the backend never persists; stripped
// from the spread payload so we only send real columns.
const RUNTIME_FIELDS = [
  'bound_channels',
  'enable_groups',
  'quota_types',
  'matched_models',
  'matched_count',
] as const

const EMPTY_VALUES: CapabilityEditValues = {
  supportsVision: null,
  contextWindow: null,
  maxOutputTokens: null,
  inputModalities: [],
  outputModalities: [],
  features: [],
}

function visionToSelect(v: boolean | null): string {
  if (v === true) return 'true'
  if (v === false) return 'false'
  return 'unset'
}

function selectToVision(v: string): boolean | null {
  if (v === 'true') return true
  if (v === 'false') return false
  return null
}

function toggle(list: string[], value: string, on: boolean): string[] {
  if (on) return list.includes(value) ? list : [...list, value]
  return list.filter((x) => x !== value)
}

interface ModelCapabilityEditDialogProps {
  open: boolean
  item: ModelFieldSyncItem | null
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}

export function ModelCapabilityEditDialog({
  open,
  item,
  onOpenChange,
  onSaved,
}: ModelCapabilityEditDialogProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [values, setValues] = useState<CapabilityEditValues>(EMPTY_VALUES)

  const needsRaw = open && !!item && item.id > 0

  // id>0: fetch the pure DB row (raw=true skips enrichment) for accurate
  // endpoints and the full payload base. staleTime:0 forces a refetch on every
  // reopen so the spread payload base is never a stale cached row; isFetching
  // (below) blocks Save until that fresh fetch lands.
  const { data: rawRes, isFetching: rawFetching } = useQuery({
    queryKey: ['model-raw', item?.id],
    queryFn: () => getModel(item!.id, { raw: true }),
    enabled: needsRaw,
    staleTime: 0,
    gcTime: 0,
  })

  const rawModel = rawRes?.data

  // Seed the form whenever the dialog opens (or the raw row arrives).
  useEffect(() => {
    if (!open || !item) return
    if (item.id > 0) {
      if (rawModel) setValues(extractEditValues(rawModel))
    } else {
      setValues(extractEditValues(item))
    }
  }, [open, item, rawModel])

  const saving = useMutation({
    mutationFn: async () => {
      if (!item) throw new Error('No item')

      // baseCaps: for id==0 use the preview item's upstream capabilities so we
      // never wipe upstream source/vision/open_weights/max_input_tokens.
      const baseCaps =
        item.id > 0 ? rawModel?.capabilities : item.capabilities
      const caps = buildCapabilities(baseCaps, values)

      if (item.id > 0) {
        if (!rawModel) throw new Error('Raw model not loaded')
        const payload: Partial<Model> & { id: number } = {
          ...rawModel,
          supports_vision: values.supportsVision,
          max_output_tokens: values.maxOutputTokens,
          context_window: values.contextWindow,
          capabilities: JSON.stringify(caps),
          field_sync_locked: true,
        }
        for (const key of RUNTIME_FIELDS) {
          delete (payload as Record<string, unknown>)[key]
        }
        return updateModel(payload)
      }

      const payload: Partial<Model> = {
        model_name: item.model_name,
        status: 1,
        sync_official: 1,
        name_rule: 0,
        supports_vision: values.supportsVision,
        max_output_tokens: values.maxOutputTokens,
        context_window: values.contextWindow,
        capabilities: JSON.stringify(caps),
        field_sync_locked: true,
      }
      return createModel(payload)
    },
    onSuccess: (res) => {
      if (!res.success) {
        toast.error(res.message || t('Failed to save'))
        return
      }
      toast.success(t('Saved'))
      // Drop the cached raw row so the next open refetches fresh and never
      // spreads a now-outdated base into a full PUT.
      queryClient.removeQueries({ queryKey: ['model-raw', item?.id] })
      onSaved()
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : t('Failed to save')
      toast.error(msg)
    },
  })

  const loading = needsRaw && rawFetching

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={item ? `${t('Edit')} - ${item.model_name}` : t('Edit')}
      contentClassName='max-h-[calc(100dvh-2rem)] overflow-hidden max-sm:w-screen max-sm:max-w-none max-sm:rounded-none max-sm:p-4 sm:max-w-2xl'
      contentHeight='auto'
      footer={
        loading ? null : (
          <>
            <Button
              type='button'
              variant='outline'
              onClick={() => onOpenChange(false)}
            >
              {t('Cancel')}
            </Button>
            <Button
              type='button'
              onClick={() => saving.mutate()}
              disabled={saving.isPending}
            >
              {saving.isPending ? (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              ) : null}
              {t('Save')}
            </Button>
          </>
        )
      }
    >
      {loading ? (
        <div className='flex items-center justify-center py-10'>
          <Loader2 className='text-muted-foreground h-6 w-6 animate-spin' />
        </div>
      ) : (
        <div className='max-h-[calc(100dvh-8.5rem)] space-y-4 overflow-y-auto py-2 pr-1 sm:max-h-[72vh]'>
          <div className='grid gap-4 sm:grid-cols-3'>
            <div className='space-y-2'>
              <Label>{t('Vision')}</Label>
              <NativeSelect
                className='w-full'
                value={visionToSelect(values.supportsVision)}
                onChange={(e) =>
                  setValues((prev) => ({
                    ...prev,
                    supportsVision: selectToVision(e.target.value),
                  }))
                }
              >
                <NativeSelectOption value='true'>{t('Yes')}</NativeSelectOption>
                <NativeSelectOption value='false'>{t('No')}</NativeSelectOption>
                <NativeSelectOption value='unset'>
                  {t('Unset')}
                </NativeSelectOption>
              </NativeSelect>
            </div>

            <div className='space-y-2'>
              <Label>{t('Context window')}</Label>
              <Input
                type='number'
                min={0}
                value={values.contextWindow ?? ''}
                onChange={(e) =>
                  setValues((prev) => ({
                    ...prev,
                    contextWindow:
                      e.target.value === '' ? null : Number(e.target.value),
                  }))
                }
              />
            </div>

            <div className='space-y-2'>
              <Label>{t('Output limit')}</Label>
              <Input
                type='number'
                min={0}
                value={values.maxOutputTokens ?? ''}
                onChange={(e) =>
                  setValues((prev) => ({
                    ...prev,
                    maxOutputTokens:
                      e.target.value === '' ? null : Number(e.target.value),
                  }))
                }
              />
            </div>
          </div>

          <div className='space-y-2'>
            <Label>{t('Input modalities')}</Label>
            <div className='flex flex-wrap gap-3'>
              {INPUT_MODALITIES.map((m) => (
                <label key={m} className='flex items-center gap-2 text-sm'>
                  <Checkbox
                    checked={values.inputModalities.includes(m)}
                    onCheckedChange={(checked) =>
                      setValues((prev) => ({
                        ...prev,
                        inputModalities: toggle(
                          prev.inputModalities,
                          m,
                          checked === true
                        ),
                      }))
                    }
                  />
                  {t(m)}
                </label>
              ))}
            </div>
          </div>

          <div className='space-y-2'>
            <Label>{t('Output modalities')}</Label>
            <div className='flex flex-wrap gap-3'>
              {OUTPUT_MODALITIES.map((m) => (
                <label key={m} className='flex items-center gap-2 text-sm'>
                  <Checkbox
                    checked={values.outputModalities.includes(m)}
                    onCheckedChange={(checked) =>
                      setValues((prev) => ({
                        ...prev,
                        outputModalities: toggle(
                          prev.outputModalities,
                          m,
                          checked === true
                        ),
                      }))
                    }
                  />
                  {t(m)}
                </label>
              ))}
            </div>
          </div>

          <div className='space-y-2'>
            <Label>{t('Special capabilities')}</Label>
            <div className='flex flex-wrap gap-3'>
              {EDITABLE_FEATURES.map((f) => (
                <label key={f} className='flex items-center gap-2 text-sm'>
                  <Checkbox
                    checked={values.features.includes(f)}
                    onCheckedChange={(checked) =>
                      setValues((prev) => ({
                        ...prev,
                        features: toggle(prev.features, f, checked === true),
                      }))
                    }
                  />
                  {t(f)}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </Dialog>
  )
}
